import React from 'react';
import {
    Box, Typography, Chip, IconButton, Tooltip, Divider, CircularProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { IEstacaoKDS, IItemPedidoKDS, StatusItemKDS } from '../../../../../../cozinha/interfaces/IKDS';
import { KDSTimer } from '../../../../../../cozinha/components/KDSTimer';

interface MiniKDSEstacaoProps {
    estacao: IEstacaoKDS;
    itens: IItemPedidoKDS[];
    pedidoUuid: string;
    readOnly: boolean;
    loadingAcao: boolean;
    onIniciar: (uuid: string) => void;
    onConcluir: (uuid: string) => void;
    onReverter: (uuid: string) => void;
    onConcluirTodos: (estacaoUuid: string) => void;
}

const STATUS_LABEL: Record<StatusItemKDS, string> = {
    [StatusItemKDS.AGUARDANDO_DISPARO]: 'Aguardando disparo',
    [StatusItemKDS.AGUARDANDO]: 'Aguardando',
    [StatusItemKDS.EM_PREPARO]: 'Em preparo',
    [StatusItemKDS.PRONTO]: 'Pronto',
    [StatusItemKDS.CANCELADO]: 'Cancelado',
    [StatusItemKDS.LIBERADO]: 'Liberado',
};

const STATUS_COLOR: Record<StatusItemKDS, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
    [StatusItemKDS.AGUARDANDO_DISPARO]: 'default',
    [StatusItemKDS.AGUARDANDO]: 'warning',
    [StatusItemKDS.EM_PREPARO]: 'info',
    [StatusItemKDS.PRONTO]: 'success',
    [StatusItemKDS.CANCELADO]: 'error',
    [StatusItemKDS.LIBERADO]: 'default',
};

const temAcaoPendente = (status: StatusItemKDS) =>
    status === StatusItemKDS.AGUARDANDO || status === StatusItemKDS.EM_PREPARO;

export const MiniKDSEstacao: React.FC<MiniKDSEstacaoProps> = ({
    estacao, itens, readOnly, loadingAcao,
    onIniciar, onConcluir, onReverter, onConcluirTodos,
}) => {
    const itensPendentes = itens.filter(i => temAcaoPendente(i.status));
    const todosProntos = itens.every(i =>
        i.status === StatusItemKDS.PRONTO ||
        i.status === StatusItemKDS.CANCELADO ||
        i.status === StatusItemKDS.LIBERADO
    );

    return (
        <Box sx={{ mb: 2 }}>
            {/* Header estação */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                borderLeft: `4px solid ${estacao.cor || '#888'}`,
                pl: 1.5, py: 0.5,
            }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    {estacao.nome}
                </Typography>
                {todosProntos && (
                    <Chip label="Pronto" size="small" color="success" />
                )}
            </Box>

            {/* Lista de itens */}
            {itens.map(item => (
                <Box key={item.uuid} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    px: 1.5, py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    mb: 0.5,
                }}>
                    {/* Descrição */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap fontWeight={500}>
                            {item.quantidade}x {item.produtoDescricao}
                            {item.processoDescricao && (
                                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                                    · {item.processoDescricao}
                                </Typography>
                            )}
                        </Typography>
                        {item.detalhesProduto?.map((d, i) => (
                            <Typography key={i} variant="caption" color="text.secondary" display="block">
                                {d.tipo}: {d.descricao}
                            </Typography>
                        ))}
                        {item.observacoes && (
                            <Typography variant="caption" color="warning.main" display="block">
                                Obs: {item.observacoes}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                            <Chip
                                label={STATUS_LABEL[item.status]}
                                size="small"
                                color={STATUS_COLOR[item.status]}
                                sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {(item.status === StatusItemKDS.EM_PREPARO || item.status === StatusItemKDS.AGUARDANDO) && (
                                <KDSTimer
                                    iniciadoEm={item.iniciadoEm}
                                    disparadoEm={item.disparadoEm}
                                    criadoEm={item.criadoEm}
                                    tempoEstimadoSegundos={item.tempoEstimadoSegundos}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Ações por item */}
                    {!readOnly && (
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                            {item.status === StatusItemKDS.AGUARDANDO && (
                                <Tooltip title="Iniciar">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={loadingAcao}
                                            onClick={() => onIniciar(item.uuid)}
                                        >
                                            {loadingAcao ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {item.status === StatusItemKDS.EM_PREPARO && (
                                <>
                                    <Tooltip title="Concluir">
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                disabled={loadingAcao}
                                                onClick={() => onConcluir(item.uuid)}
                                            >
                                                {loadingAcao ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Reverter">
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={loadingAcao}
                                                onClick={() => onReverter(item.uuid)}
                                            >
                                                <UndoIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                            {item.status === StatusItemKDS.PRONTO && (
                                <Tooltip title="Reverter">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={loadingAcao}
                                            onClick={() => onReverter(item.uuid)}
                                        >
                                            <UndoIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </Box>
            ))}

            {/* Tudo pronto */}
            {!readOnly && itensPendentes.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 1 }}>
                    <Tooltip title="Marcar todos como prontos">
                        <span>
                            <IconButton
                                size="small"
                                color="success"
                                disabled={loadingAcao}
                                onClick={() => onConcluirTodos(estacao.uuid)}
                                sx={{ border: '1px solid', borderColor: 'success.main', borderRadius: 1, gap: 0.5, px: 1 }}
                            >
                                <DoneAllIcon fontSize="small" />
                                <Typography variant="caption" color="success.main" fontWeight="bold">
                                    Tudo pronto
                                </Typography>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            <Divider sx={{ mt: 1.5 }} />
        </Box>
    );
};
